import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Save } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';
import {
  useCoachBuildMutation,
  useMeQuery,
  useSaveDeckMutation,
} from '@/services/qbotApi';
import { Paywall } from '@/components/cr/Paywall';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

const SUGGESTED_STYLES = ['Beatdown', 'Cycle', 'Control', 'Siege', 'Bait', 'Bridge Spam'];

/**
 * AI deck builder — given a free-form style hint, returns 8 cards + strategy.
 * The keptCards mode (suggest the remaining N) is intentionally deferred:
 * it needs the card-name picker which doesn't ship in slice 3.
 *
 * Pro-gated; free → Paywall. Rate limit 5/h server-side.
 */
export function CRDeckBuilder() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const { data: identity, isLoading: identityLoading } = useMeQuery();
  const [style, setStyle] = useState('');
  const [build, { data, isLoading, error, reset }] = useCoachBuildMutation();
  const [saveDeck, { isLoading: saving, isSuccess: savedOk }] = useSaveDeckMutation();

  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';
  const canSubmit = style.trim().length > 0 && !isLoading;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    haptics.impact('medium');
    reset();
    build({ style: style.trim() }).catch(() => undefined);
  };

  useMainButton({
    text: data ? t('builder.cta.regenerate') : t('builder.cta.generate'),
    enabled: canSubmit,
    loading: isLoading,
    onClick: () => submit(),
  });

  if (identityLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('builder.loading')}</div>;
  }

  if (!isPro) {
    return (
      <div className="flex h-full items-center justify-center px-4">
        <Paywall feature={t('builder.title')} />
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4"
    >
      <header>
        <h1 className="text-xl font-bold text-fg">{t('builder.title')}</h1>
        <p className="text-[11px] text-fg-muted">{t('builder.subtitle')}</p>
      </header>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('builder.field.label')}
        </span>
        <input
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          placeholder={t('builder.field.placeholder')}
          maxLength={120}
          className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
        />
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pt-1">
          {SUGGESTED_STYLES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStyle(s)}
              className="shrink-0 rounded-full border border-border bg-bg-secondary px-3 py-1 text-[11px] text-fg-muted transition-colors active:bg-bg-tertiary"
            >
              {s}
            </button>
          ))}
        </div>
      </label>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-bg-secondary p-6 text-sm text-fg-muted">
          <Sparkles className="h-4 w-4 animate-pulse text-brand" />
          {t('builder.thinking')}
        </div>
      )}

      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {builderErrorMessage(error, t)}
        </p>
      )}

      {data?.deck && (
        <section className="flex flex-col gap-3 rounded-xl bg-bg-secondary p-3">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-brand">
              {data.deck.archetype}
            </span>
            <span className="text-[11px] text-fg-muted">
              ⚡ {data.deck.avgElixir}
            </span>
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {data.deck.cards.map((c, i) => (
              <li
                key={`${c}-${i}`}
                className="rounded-md bg-bg-tertiary px-2 py-1 text-[11px] text-fg"
              >
                {c}
              </li>
            ))}
          </ul>
          <p className="whitespace-pre-line text-sm text-fg-muted">
            {data.deck.strategy}
          </p>
          <button
            type="button"
            disabled={saving || data.deck.cards.length < 8}
            onClick={async () => {
              try {
                await saveDeck({
                  name: `AI ${data.deck.archetype}`,
                  cards: data.deck.cards.slice(0, 8),
                }).unwrap();
                haptics.notification('success');
              } catch {
                haptics.notification('error');
              }
            }}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-brand py-2 text-xs font-bold uppercase tracking-wide text-brand-fg disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {savedOk ? t('builder.saved') : t('builder.save')}
          </button>
          {data.usage && (
            <p className="text-[10px] text-fg-muted">
              {t('builder.usage', {
                model: data.usage.model,
                cost: data.usage.costUsd.toFixed(4),
              })}
            </p>
          )}
        </section>
      )}

      {/* Inline submit kept for browser-dev mode without MainButton */}
      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-auto rounded-lg bg-brand py-3 text-sm font-bold uppercase tracking-wide text-brand-fg disabled:opacity-40"
      >
        {isLoading ? t('builder.cta.loading') : t('builder.cta.generate')}
      </button>
    </form>
  );
}

function builderErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (!isQbotApiError(err)) return t('builder.error.generic');
  const code = err.data?.error?.code;
  if (code === 'QUOTA_EXCEEDED') {
    const details = err.data?.error?.details as { retryAfterSeconds?: number } | undefined;
    const seconds = details?.retryAfterSeconds ?? 0;
    return t('builder.error.quota', { minutes: Math.max(1, Math.ceil(seconds / 60)) });
  }
  if (code === 'TIER_REQUIRED') return t('builder.error.tier');
  return t('builder.error.generic');
}
